/**
 * Chat-message attachment size formatter.
 *
 * Returns a locale-aware string like `"2.4 MB"`, `"512 KB"`, or `"15 B"`.
 * The numeric portion is formatted via `Intl.NumberFormat(locale, ...)` so
 * decimal + grouping separators follow the active UI locale; the unit
 * (`B` / `KB` / `MB` / `GB`) stays English (universal — matches the
 * existing legacy + new-stack rendering in clients / card attachment tiles).
 *
 * Callers thread `i18n.language` from `useTranslation('chat')`. Don't
 * reach for module-level `Intl.NumberFormat(undefined, ...)` singletons —
 * those capture the system locale at import time and never re-evaluate
 * (see `[FE/i18n]` Intl-singleton pitfall in `3mote-fe-new/CLAUDE.md`).
 */

const KB = 1024
const MB = KB * 1024
const GB = MB * 1024

export function formatBytes(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B'
  if (bytes < KB) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(bytes)} B`
  }
  if (bytes < MB) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(bytes / KB))} KB`
  }
  if (bytes < GB) {
    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(bytes / MB)} MB`
  }
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(bytes / GB)} GB`
}
